import React, { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, Music, TrendingUp, ChevronDown } from "lucide-react";

export default function PurchasesManager({ db }) {
  const [purchases, setPurchases] = useState([]);
  const [totalSales, setTotalSales] = useState({ usd: 0, php: 0 });
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [expandedMethods, setExpandedMethods] = useState({
    paypal: true,
    gcash: false,
    qrph: false
  });

  // Payment method constants
  const PAYMENT_METHODS = {
    PAYPAL: 'paypal',
    GCASH: 'gcash',
    QRPH: 'qrph'
  };

  // Helper function to determine currency from payment method
  const getCurrency = (paymentMethod) => {
    if (paymentMethod === PAYMENT_METHODS.GCASH || paymentMethod === PAYMENT_METHODS.QRPH) {
      return 'PHP';
    }
    return 'USD';
  };

  // Helper function to format amount with currency
  const formatAmount = (amount, paymentMethod) => {
    const currency = getCurrency(paymentMethod);
    const numAmount = parseFloat(amount) || 0;

    if (currency === 'PHP') {
      return `₱${numAmount.toFixed(2)}`;
    }
    return `$${numAmount.toFixed(2)}`;
  };

  // Helper function to get payment method label and icon
  const getPaymentMethodLabel = (paymentMethod) => {
    switch (paymentMethod) {
      case PAYMENT_METHODS.PAYPAL:
        return { label: 'PayPal', icon: '💳', color: 'bg-blue-500/15 hover:bg-blue-500/25 border-blue-500/30 text-blue-200' };
      case PAYMENT_METHODS.GCASH:
        return { label: 'GCash', icon: '📱', color: 'bg-sky-500/15 hover:bg-sky-500/25 border-sky-500/30 text-sky-200' };
      case PAYMENT_METHODS.QRPH:
        return { label: 'QR PH', icon: '📱', color: 'bg-indigo-500/15 hover:bg-indigo-500/25 border-indigo-500/30 text-indigo-200' };
      default:
        return { label: paymentMethod?.toUpperCase() || 'Unknown', icon: '?', color: 'bg-[#1e1e25] hover:bg-[#26262d] border-[#2a2a33] text-[#a6a6b5]' };
    }
  };

  useEffect(() => {
    const purchasesRef = ref(db, "purchases/");
    onValue(purchasesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const purchasesArray = Object.values(data);
        setPurchases(purchasesArray);

        // Calculate totals by currency
        const totals = { usd: 0, php: 0 };
        purchasesArray.forEach((p) => {
          const amount = parseFloat(p.price) || 0;
          const paymentMethod = p.paymentMethod || 'paypal';

          if (paymentMethod === PAYMENT_METHODS.GCASH || paymentMethod === PAYMENT_METHODS.QRPH) {
            totals.php += amount;
          } else {
            totals.usd += amount;
          }
        });

        setTotalSales(totals);
        setTotalTransactions(purchasesArray.length);
      }
    });
  }, [db, PAYMENT_METHODS.GCASH, PAYMENT_METHODS.QRPH]);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-[#243137] p-4 sm:p-6 rounded-xl shadow-lg text-white flex items-center gap-3 sm:gap-4 border border-[#bd9f67]/20 hover:border-[#bd9f67]/40 hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] transition`}
    >
      <div className="bg-[#bd9f67]/15 p-2 sm:p-4 rounded-lg flex-shrink-0">
        <Icon size={24} className="sm:w-8 sm:h-8 text-[#bd9f67]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm opacity-90 truncate">{label}</p>
        <p className="text-xl sm:text-3xl font-bold truncate">{value}</p>
      </div>
    </motion.div>
  );

  // Calculate total sales value display
  const getTotalSalesDisplay = () => {
    if (totalSales.php > 0 && totalSales.usd > 0) {
      return `$${totalSales.usd.toFixed(2)} + ₱${totalSales.php.toFixed(2)}`;
    }
    if (totalSales.php > 0) {
      return `₱${totalSales.php.toFixed(2)}`;
    }
    return `$${totalSales.usd.toFixed(2)}`;
  };

  // Calculate average sale
  const getAverageSale = () => {
    if (totalTransactions === 0) return '$0.00';
    
    const totalUSD = totalSales.usd;
    const avg = totalUSD / totalTransactions;
    
    if (totalSales.php > 0 && totalSales.usd === 0) {
      const phpAvg = totalSales.php / totalTransactions;
      return `₱${phpAvg.toFixed(2)}`;
    }
    
    return `$${avg.toFixed(2)}`;
  };

  // Filter purchases by payment method
  const getPurchasesByMethod = (method) => {
    return purchases.filter(p => (p.paymentMethod || 'paypal') === method);
  };

  // Calculate total for specific payment method
  const getTotalByMethod = (method) => {
    return getPurchasesByMethod(method).reduce((sum, p) => {
      return sum + (parseFloat(p.price) || 0);
    }, 0);
  };

  // Toggle expansion
  const toggleMethod = (method) => {
    setExpandedMethods(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };

  // Render purchase table for a specific payment method
  const PurchaseTable = ({ paymentMethod }) => {
    const methodInfo = getPaymentMethodLabel(paymentMethod);
    const methodPurchases = getPurchasesByMethod(paymentMethod);
    const total = getTotalByMethod(paymentMethod);
    const isExpanded = expandedMethods[paymentMethod];

    if (methodPurchases.length === 0) {
      return null;
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-[#16161b] shadow rounded-lg overflow-hidden border border-[#bd9f67]/15"
      >
        {/* Collapsible Header */}
        <button
          onClick={() => toggleMethod(paymentMethod)}
          className={`w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between ${methodInfo.color} border-b border-[#2a2a33] transition`}
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <span className="text-2xl flex-shrink-0">{methodInfo.icon}</span>
            <div className="min-w-0">
              <p className="font-semibold text-[#f9f9f9] text-sm sm:text-base text-left">
                {methodInfo.label}
              </p>
              <p className="text-xs text-[#a6a6b5]">
                {methodPurchases.length} transaction{methodPurchases.length !== 1 ? 's' : ''} • {formatAmount(total, paymentMethod)}
              </p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0 ml-2"
          >
            <ChevronDown size={20} className="text-[#a6a6b5]" />
          </motion.div>
        </button>

        {/* Table Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#243137] border-b border-[#2a2a33]">
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67]">Email</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67] hidden sm:table-cell">Music</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67]">Amount</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67] hidden md:table-cell">Date</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2a2a33]">
                    {methodPurchases.map((p, index) => (
                      <motion.tr
                        key={p.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="hover:bg-[#1e1e25] transition duration-150"
                      >
                        <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm text-[#f9f9f9]">
                          <span className="font-medium break-all">{p.email}</span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm text-[#c9c9d4] hidden sm:table-cell">
                          <div className="flex items-center gap-1 truncate">
                            <Music size={14} className="text-[#bd9f67] flex-shrink-0" />
                            <span className="truncate">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm font-semibold">
                          <span className={getCurrency(paymentMethod) === 'PHP' ? 'text-[#bd9f67]' : 'text-[#bd9f67]'}>
                            {formatAmount(p.price, paymentMethod)}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm text-[#a6a6b5] hidden md:table-cell whitespace-nowrap">
                          {new Date(p.timestamp).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-3 sm:px-6 py-3 text-xs">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                              p.status === "completed"
                                ? "bg-green-500/15 text-green-200"
                                : "bg-red-500/15 text-red-200"
                            }`}
                          >
                            {p.status === "completed" ? "✓" : "✗"}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="px-3 sm:px-6 py-3 bg-[#1e1e25] border-t border-[#2a2a33] text-xs sm:text-sm">
                <p className="text-[#f9f9f9] font-semibold">
                  Subtotal: <span className={getCurrency(paymentMethod) === 'PHP' ? 'text-[#bd9f67]' : 'text-[#bd9f67]'}>{formatAmount(total, paymentMethod)}</span>
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          icon={DollarSign}
          label="Total Sales"
          value={getTotalSalesDisplay()}
          color="bg-[#bd9f67]/15"
        />
        <StatCard
          icon={TrendingUp}
          label="Transactions"
          value={totalTransactions}
          color="bg-[#bd9f67]/15"
        />
        <StatCard
          icon={Music}
          label="Avg Sale"
          value={getAverageSale()}
          color="bg-[#bd9f67]/15"
        />
      </div>

      {/* Payment Method Tables */}
      <div className="space-y-3 sm:space-y-4">
        <PurchaseTable paymentMethod={PAYMENT_METHODS.PAYPAL} />
        <PurchaseTable paymentMethod={PAYMENT_METHODS.GCASH} />
        <PurchaseTable paymentMethod={PAYMENT_METHODS.QRPH} />
      </div>

      {/* Empty State */}
      {purchases.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-[#16161b] shadow rounded-lg p-8 sm:p-12 text-center border border-[#bd9f67]/15"
        >
          <Music size={40} className="mx-auto mb-3 text-[#7d7d8a]" />
          <p className="text-[#7d7d8a]">No purchases yet</p>
        </motion.div>
      )}
    </div>
  );
}